/**
 * Shared domain types used by more than one screen.
 *
 * Names and shapes mirror `docs/ddd/20-ubiquitous-language.md`. When the real
 * OpenAPI-generated client ships (ADR-0013 "Translation strategy"), these
 * hand-written aliases go away; the generated types replace them.
 */

/**
 * Vendor enum from BRD §10 / `docs/ddd/20-ubiquitous-language.md`. The UI
 * displays the human label; the backend stores the enum tag.
 */
export type VendorId = 'Fccm' | 'Actimize' | 'Sas' | 'Verafin' | 'Fis' | 'FcrmFiserv' | 'Quantexa';

export const VENDOR_LABELS: Record<VendorId, string> = {
  Fccm: 'Oracle FCCM',
  Actimize: 'NICE Actimize',
  Sas: 'SAS AML',
  Verafin: 'Verafin',
  Fis: 'FIS',
  FcrmFiserv: 'Fiserv FCRM',
  Quantexa: 'Quantexa',
};

/**
 * Scenario lifecycle status (BC-01 — invariants on `ScenarioVersion.status`).
 * `Draft` and `Published` are the two real values from the domain; `Retired`
 * is surfaced by `RetireScenario` command. UI shows them as uppercase chips.
 */
export type ScenarioStatus = 'DRAFT' | 'PUBLISHED' | 'RETIRED';

/**
 * Drift posture of a scenario — aggregated from BC-05 rule-drift surface
 * (design `app/RuleDrift.html`; ADR-0017). `NONE` means no open drift record.
 */
export type DriftStatus = 'NONE' | 'MONITOR' | 'REVIEW' | 'OPEN';

/**
 * Replay run lifecycle — matches `ValidationRun.run_status` transitions
 * documented in `docs/ddd/10-context-replay-execution.md`. The UI-facing
 * strings are uppercase to align with the design chip copy.
 */
export type RunStatus =
  | 'RUNNING'
  | 'AWAITING SIG'
  | 'SEALED'
  | 'FAILED'
  | 'UNDER REVIEW'
  | 'MONITOR';

/**
 * HistoricalAlert disposition (BRD §17.5 field 7). The ubiquitous language
 * collapses source-platform disposition enums into this small canonical set;
 * upstream vendor strings are kept verbatim in the alert's `rawPayload`.
 */
export type AlertDisposition =
  | 'OPEN'
  | 'UNDER_REVIEW'
  | 'ESCALATED'
  | 'CLOSED_SAR_FILED'
  | 'CLOSED_NO_ACTION'
  | 'FALSE_POSITIVE';

/** Alert severity class shown on the live-stream chips. */
export type AlertClass = 'LOW' | 'MED' | 'HIGH';

/**
 * Comparison outcome for a single alert (BRD §19 — the matrix axes).
 * A divergence row is one of these four variants; the rest of the row
 * explains the shape of the disagreement.
 */
export type DivergenceOutcome = 'MATCHED' | 'MISSED' | 'EXTRA' | 'THRESHOLD_DRIFT';

export const DIVERGENCE_LABELS: Record<DivergenceOutcome, string> = {
  MATCHED: 'Matched',
  MISSED: 'Missed',
  EXTRA: 'Extra',
  THRESHOLD_DRIFT: 'Threshold drift',
};

export const ALERT_DISPOSITION_LABELS: Record<AlertDisposition, string> = {
  OPEN: 'Open',
  UNDER_REVIEW: 'Under review',
  ESCALATED: 'Escalated',
  CLOSED_SAR_FILED: 'Closed · SAR filed',
  CLOSED_NO_ACTION: 'Closed · no action',
  FALSE_POSITIVE: 'False positive',
};

/**
 * Model lifecycle (BRD §30 — model registry). The console surfaces these as
 * uppercase chips. `AWAITING_SIGN` represents a model version that passed
 * training gates but has not yet collected the 3-of-3 quorum signatures.
 */
export type ModelStatus = 'PROD' | 'STAGING' | 'TRAINING' | 'AWAITING_SIGN' | 'RETIRED';

export const MODEL_STATUS_LABELS: Record<ModelStatus, string> = {
  PROD: 'Prod',
  STAGING: 'Staging',
  TRAINING: 'Training',
  AWAITING_SIGN: 'Awaiting sign',
  RETIRED: 'Retired',
};

/**
 * Attestation lifecycle — mirrors ADR-0009 (evidence signing / immutable runs).
 * A `PENDING` attestation has 0 signatures; `PARTIAL` has 1..n-1; `SEALED` is
 * the final m-of-n terminal state.
 */
export type AttestationStatus = 'PENDING' | 'PARTIAL' | 'SEALED' | 'EXPIRED';

export const ATTESTATION_STATUS_LABELS: Record<AttestationStatus, string> = {
  PENDING: 'Pending',
  PARTIAL: 'Partial',
  SEALED: 'Sealed',
  EXPIRED: 'Expired',
};

/**
 * Audit package export lifecycle (BRD §15). `SEALED` packages are immutable
 * and carry the 32-byte merkle root; `SHARED` means the bundle has been
 * delivered to the named regulator at least once.
 */
export type AuditPackageStatus = 'DRAFT' | 'SEALED' | 'SHARED' | 'EXPIRED';

export const AUDIT_PACKAGE_STATUS_LABELS: Record<AuditPackageStatus, string> = {
  DRAFT: 'Draft',
  SEALED: 'Sealed',
  SHARED: 'Shared',
  EXPIRED: 'Expired',
};

/**
 * Signer / quorum rotation trigger (ADR-0009 — quorum key rotation policy).
 * `SCHEDULED` is the 90-day automatic rotation; `INCIDENT` is a break-glass
 * rotation initiated by a named actor.
 */
export type KeyRotationReason = 'SCHEDULED' | 'INCIDENT' | 'COMPROMISE' | 'MIGRATION';

export const KEY_ROTATION_REASON_LABELS: Record<KeyRotationReason, string> = {
  SCHEDULED: 'Scheduled 90d',
  INCIDENT: 'Incident response',
  COMPROMISE: 'Compromise',
  MIGRATION: 'Migration',
};

/**
 * Operator role (ADR-0010 `sentinel-auth::Role`). The six variants mirror the
 * Rust enum exactly so OpenAPI-generated clients can drop in later without
 * reshaping the UI. Display labels live in `ROLE_LABELS` for chip copy.
 */
export type Role =
  | 'ValidationAnalyst'
  | 'ValidationManager'
  | 'ComplianceReviewer'
  | 'InternalAuditor'
  | 'PlatformAdmin'
  | 'Reader';

export const ROLE_LABELS: Record<Role, string> = {
  ValidationAnalyst: 'Validation analyst',
  ValidationManager: 'Validation manager',
  ComplianceReviewer: 'Compliance reviewer',
  InternalAuditor: 'Internal auditor',
  PlatformAdmin: 'Platform admin',
  Reader: 'Reader',
};

/**
 * Tenant data mode (BRD §14 — data residency / Mode A live, Mode B hybrid,
 * Mode C synthetic). Governs which `SourceAdapter` implementations the tenant
 * is allowed to wire up (ADR-0011 synthetic, ADR-0006 live adapters).
 */
export type DataMode = 'LIVE' | 'HYBRID' | 'SYNTHETIC';

export const DATA_MODE_LABELS: Record<DataMode, string> = {
  LIVE: 'Live · in-region',
  HYBRID: 'Hybrid · sampled synthetic',
  SYNTHETIC: 'Synthetic · pilot only',
};

/** Team-member lifecycle surfaced on the Team screen. */
export type MemberStatus = 'ACTIVE' | 'INVITED' | 'SUSPENDED' | 'EXPIRING';

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  ACTIVE: 'Active',
  INVITED: 'Invited',
  SUSPENDED: 'Suspended',
  EXPIRING: 'Expiring',
};

/** Multi-factor bearer on a team-member account (design copy). */
export type MfaMode = 'HARDWARE_KEY' | 'TOTP' | 'NONE';

export const MFA_MODE_LABELS: Record<MfaMode, string> = {
  HARDWARE_KEY: 'Hardware key',
  TOTP: 'TOTP',
  NONE: 'None',
};
